"use client";

import React, { useState } from 'react';
import { ArrowRight, ShieldCheck, FileText, Network } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const ContractGraph = () => {
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  // Static node positions (x, y coordinates in viewBox 0-800 / 0-400)
  const nodes = [{
    id: "Contratto",
    x: 400,
    y: 200,
    group: 1,
    radius: 12
  }, {
    id: "Parti",
    x: 280,
    y: 150,
    group: 2,
    radius: 8
  }, {
    id: "Oggetto",
    x: 280,
    y: 250,
    group: 2,
    radius: 8
  }, {
    id: "Corrispettivo",
    x: 520,
    y: 150,
    group: 2,
    radius: 8
  }, {
    id: "Durata",
    x: 520,
    y: 250,
    group: 2,
    radius: 8
  }, {
    id: "Recesso",
    x: 180,
    y: 100,
    group: 3,
    radius: 6
  }, {
    id: "Riservatezza",
    x: 350,
    y: 80,
    group: 3,
    radius: 6
  }, {
    id: "Penali",
    x: 620,
    y: 100,
    group: 3,
    radius: 6
  }, {
    id: "Foro",
    x: 450,
    y: 80,
    group: 3,
    radius: 6
  }, {
    id: "Legge",
    x: 550,
    y: 80,
    group: 3,
    radius: 6
  }, {
    id: "Allegato A",
    x: 200,
    y: 320,
    group: 4,
    radius: 4
  }, {
    id: "Allegato B",
    x: 360,
    y: 320,
    group: 4,
    radius: 4
  }];
  const links = [{
    source: "Contratto",
    target: "Parti"
  }, {
    source: "Contratto",
    target: "Oggetto"
  }, {
    source: "Contratto",
    target: "Corrispettivo"
  }, {
    source: "Contratto",
    target: "Durata"
  }, {
    source: "Parti",
    target: "Recesso"
  }, {
    source: "Oggetto",
    target: "Allegato A"
  }, {
    source: "Oggetto",
    target: "Allegato B"
  }, {
    source: "Corrispettivo",
    target: "Penali"
  }, {
    source: "Durata",
    target: "Recesso"
  }, {
    source: "Contratto",
    target: "Riservatezza"
  }, {
    source: "Contratto",
    target: "Foro"
  }, {
    source: "Contratto",
    target: "Legge"
  }];
  const getNodeById = (id: string) => nodes.find(n => n.id === id);
  const getNodeColor = (group: number) => {
    if (group === 1) return "var(--primary)";
    if (group === 2) return "var(--accent)";
    return "var(--muted-foreground)";
  };
  return <div className="w-full h-[400px] relative overflow-hidden">
      <svg viewBox="0 0 800 400" className="w-full h-full block">
        {/* Links */}
        <g stroke="var(--border)" strokeOpacity="0.4">
          {links.map((link, i) => {
          const source = getNodeById(link.source);
          const target = getNodeById(link.target);
          if (!source || !target) return null;
          return <line key={i} x1={source.x} y1={source.y} x2={target.x} y2={target.y} strokeWidth="1.5" className="transition-all duration-300" style={{
            strokeOpacity: hoveredNode && (hoveredNode === link.source || hoveredNode === link.target) ? 0.8 : 0.4
          }} />;
        })}
        </g>

        {/* Nodes */}
        <g>
          {nodes.map(node => <g key={node.id}>
              <circle cx={node.x} cy={node.y} r={node.radius} fill={getNodeColor(node.group)} stroke="var(--background)" strokeWidth="1.5" className="transition-all duration-300 cursor-pointer" style={{
            transform: hoveredNode === node.id ? 'scale(1.2)' : 'scale(1)',
            transformOrigin: `${node.x}px ${node.y}px`
          }} onMouseEnter={() => setHoveredNode(node.id)} onMouseLeave={() => setHoveredNode(null)} />
              {node.radius > 5 && <text x={node.x + 15} y={node.y + 4} fontSize="10" fill="var(--foreground)" fontFamily="var(--font-sans)" style={{
            pointerEvents: 'none',
            opacity: 0.8
          }}>
                  {node.id}
                </text>}
            </g>)}
        </g>
      </svg>
      {/* Gradient overlay */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-card/10 to-transparent"></div>
    </div>;
};
export default function ArchitetturaContrattuale() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  return <section id="architettura-contrattuale" className="relative w-full py-24 lg:py-32 overflow-hidden bg-background">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(222,47%,11%)] to-[hsl(217,33%,22%)] z-0"></div>
      
      {/* Noise texture overlay */}
      <div className="absolute inset-0 opacity-20 z-0 pointer-events-none bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559159/img/noise_texture.png')] mix-blend-overlay"></div>

      {/* Decorative lines */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-px h-full bg-white/5"></div>
        <div className="absolute top-1/3 left-0 w-full h-px bg-white/5"></div>
        <div className="absolute bottom-1/4 right-0 w-2/3 h-px bg-white/5"></div>
      </div>

      <div className="container relative z-10 mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {/* Left Content Column */}
          <div ref={sectionRef} className={`flex flex-col gap-8 transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 w-fit">
                <ShieldCheck className="w-4 h-4 text-primary" />
                <span className="text-xs font-medium tracking-wider uppercase text-primary-foreground/80">Precisione Legale</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium text-white leading-tight">RESPONSABILITÀ
<div>ENTI (231)
</div><div>Architettura del Diritto</div></h1>
              
              <p className="text-lg text-gray-300 max-w-xl leading-relaxed font-light">
                In DCLegal, ogni contratto è un'opera di ingegneria giuridica. Progettiamo accordi solidi, precisi e su misura, dove ogni clausola è un pilastro strutturale per la sicurezza del tuo business.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 border-none shadow-lg shadow-primary/20">
                <Link to="/ConsultingPage">
                  Richiedi Consulenza <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white backdrop-blur-sm">
                <Link to="/StudioPage">
                  Il Nostro Metodo
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-6 pt-8 border-t border-white/10">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-primary">
                  <FileText className="w-5 h-5" />
                  <span className="font-medium text-white">Analisi Strategica</span>
                </div>
                <p className="text-sm text-gray-400">Valutazione approfondita dei rischi e delle opportunità commerciali.</p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-primary">
                  <Network className="w-5 h-5" />
                  <span className="font-medium text-white">Struttura Solida</span>
                </div>
                <p className="text-sm text-gray-400">Integrazione perfetta tra le diverse clausole e normative vigenti.</p>
              </div>
            </div>
          </div>

          {/* Right Visualization Column */}
          <div className={`relative transition-all duration-1000 delay-300 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            {/* Glowing orbs */}
            <div className="absolute -top-10 -right-10 w-64 h-64 bg-primary/20 rounded-full blur-3xl opacity-30 animate-pulse"></div>
            <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl opacity-30"></div>

            <Card className="relative overflow-hidden border-white/10 bg-white/5 backdrop-blur-md shadow-2xl shadow-black/50">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50"></div>
              
              <CardContent className="p-0">
                <div className="p-6 border-b border-white/5 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
                  </div>
                  <div className="text-xs font-mono text-white/40 uppercase tracking-widest">
                    Contract_Visualization_v1.0
                  </div>
                </div>
                
                {/* Graph visualization */}
                <div className="bg-black/20 min-h-[400px]">
                  <ContractGraph />
                </div>

                <div className="p-6 border-t border-white/5 bg-white/5">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-300">
                      <span className="text-primary font-mono mr-2">●</span>
                      Nodi attivi: <span className="text-white font-mono">12</span>
                    </div>
                    <div className="text-sm text-gray-300">
                      <span className="text-blue-400 font-mono mr-2">●</span>
                      Connessioni: <span className="text-white font-mono">15</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}